// covered-by: qa/rickrubin/tests/specs/cca/ask.puntt.campaign.spec.ts
import {
  ArrowLeft,
  CaretRight,
  CircleNotch,
  UserPlus,
} from "@phosphor-icons/react";
import { Button, Dialog, Flex, Text, TextArea } from "@radix-ui/themes";
// eslint-disable-next-line import/named
import { useQueryClient } from "@tanstack/react-query";
import {
  Link,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  type ImperativePanelGroupHandle,
  Panel,
  PanelGroup,
} from "react-resizable-panels";

import AskPunttChat from "./chat";
import { CampaignChatDetailHeader } from "./components/CampaignChatDetailHeader";
import { CampaignChatList } from "./components/CampaignChatList";
import { CampaignChatPrivacyBanner } from "./components/CampaignChatPrivacyBanner";
import { CampaignDocsPanel } from "./components/CampaignDocsPanel";
import { CampaignNewChatDraft } from "./components/CampaignNewChatDraft";
import { CampaignReviewPane } from "./components/CampaignReviewPane";
import { ChevronNavButton } from "./components/ChevronNavButton";
import { CollapsedPaneLabel } from "./components/CollapsedPaneLabel";
import { ProjectInviteDialog } from "./components/ProjectInviteDialog";
import { ProjectReviewsPanel } from "./components/ProjectReviewsPanel";
import { ResizeHandle } from "./components/ResizeHandle";
import { ResizeHintTooltip } from "./components/ResizeHintTooltip";
import { useContainerWidth } from "./hooks/useContainerWidth";
import { usePersistedLayout } from "./hooks/usePersistedLayout";
import { useUiPrefs } from "./hooks/useUiPrefs";
import {
  enrichProjectReviewsFromFiles,
  projectReferenceDocumentRows,
} from "./utils/projectContextDocuments";
import { timelineHasPendingAssistant } from "./utils/projectFeedTimeline";
import {
  PROJECT_RAIL_WIDTH_PX,
  REVIEW_CHAT_FALLBACK_PX,
  reviewWorkspaceMinWidth,
  reviewWorkspaceRowStyle,
} from "./utils/projectWorkspaceLayout";
import {
  isAgentTurnEvent,
  renderProjectFeedCard,
} from "./utils/renderProjectFeedCard";

import { AvatarWithInitials } from "../../components/AvatarWithInitials";
import { useAnalytics } from "../../utils/analytics";
import { useAppDispatch, useAppSelector } from "../../utils/hooks";
import { useCreateAskPunttChat } from "../../utils/queries/askPuntt";
import {
  askPunttProjectKeys,
  useAskPunttProject,
  useProjectReviews,
  useProjectTimeline,
  useResolveFeedSuggestion,
  useShareProjectChat,
  useUpdateAskPunttProject,
} from "../../utils/queries/askPunttProjects";
import { setDrawerOpen } from "../../utils/slices/ui";

// Route for the Projects launcher (back-link target). Kept as a literal so
// this component imports no route module — that keeps it embeddable and lets
// its test mock only @tanstack/react-router (no sibling-module mocks).
const PROJECTS_LAUNCHER_PATH = "/ask-puntt/projects";

type ProjectTab = "feed" | "chats";

const FEED_POLL_INTERVAL_MS = 20_000;
const FEED_AGENT_POLL_INTERVAL_MS = 2_000;

/** Sentinel id for the inline new-chat draft (no persisted chat yet). */
export const NEW_CHAT_DRAFT_ID = "__new__";

export default function AskPunttCampaign() {
  // Loose param read so the component carries no route-module import (mirrors
  // AskPunttChat). The campaign route always supplies `projectId`.
  const { projectId } = useParams({ strict: false }) as { projectId: string };
  const { chatId: deepLinkChatId } = useSearch({ strict: false }) as {
    chatId?: string;
  };
  const navigate = useNavigate();
  const analytics = useAnalytics("ask_puntt_campaign");
  const dispatch = useAppDispatch();
  const currentUserId = useAppSelector((state) => state.auth.value?.userID);
  const appSidebarOpen = useAppSelector((state) => state.ui.drawerOpen.app);

  const [activeTab, setActiveTab] = useState<ProjectTab>("feed");

  // Poll the project on the feed tab so the Documents & context panel reflects
  // files the agent adds without a manual refresh (it otherwise renders a stale
  // snapshot while the reviews panel polls independently).
  const { data, isLoading, isError } = useAskPunttProject(projectId, {
    refetchInterval: activeTab === "feed" ? FEED_POLL_INTERVAL_MS : false,
  });
  const createChat = useCreateAskPunttChat();
  const queryClient = useQueryClient();

  const timelineQuery = useProjectTimeline(projectId, {
    enabled: activeTab === "feed",
    refetchInterval: (query) => {
      if (activeTab !== "feed") return false;
      const events = query.state.data?.events ?? [];
      if (timelineHasPendingAssistant(events))
        return FEED_AGENT_POLL_INTERVAL_MS;
      return FEED_POLL_INTERVAL_MS;
    },
  });
  const reviewsQuery = useProjectReviews(projectId, {
    refetchInterval: activeTab === "feed" ? FEED_POLL_INTERVAL_MS : false,
  });
  const resolveSuggestion = useResolveFeedSuggestion(projectId);
  const updateProject = useUpdateAskPunttProject(projectId);
  const shareChat = useShareProjectChat(projectId);

  // The shared-chat agent can mutate the project (description/context/files).
  // When the embedded chat reports a project-mutating turn, refetch the project
  // + feed so the panels update without a manual refresh.
  const handleProjectMutated = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: askPunttProjectKeys.project(projectId),
    });
    queryClient.invalidateQueries({
      queryKey: askPunttProjectKeys.feed(projectId),
    });
    queryClient.invalidateQueries({
      queryKey: askPunttProjectKeys.timeline(projectId),
    });
    // A project-mutating turn (e.g. a new create_ticket review) also refreshes
    // the right-hand Reviews panel so the new ticket bubble appears at once.
    queryClient.invalidateQueries({
      queryKey: askPunttProjectKeys.reviews(projectId),
    });
  }, [queryClient, projectId]);

  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  // The review ticket currently open in the dedicated review screen (feed
  // narrowed on the left, artwork center, Comments + Context bubbles right).
  const [activeTicketId, setActiveTicketId] = useState<string | null>(null);
  // Lets the user collapse the narrow feed column while reviewing to give the
  // artwork more room.
  const [reviewChatCollapsed, setReviewChatCollapsed] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editDescription, setEditDescription] = useState("");
  const [reviewsSectionOpen, setReviewsSectionOpen] = useState(true);
  const [contextSectionOpen, setContextSectionOpen] = useState(true);
  const rightRailExpanded = reviewsSectionOpen || contextSectionOpen;

  const project = data?.project;
  const chats = useMemo(() => data?.chats ?? [], [data?.chats]);
  const sharedChatId =
    timelineQuery.data?.sharedChatId ?? project?.sharedChatId ?? null;

  // Project-only feed events to interleave into the live shared chat on the
  // Project feed tab. Agent turns are excluded — they render as the shared
  // chat's real messages (full streaming/steps/canvas) via the embedded
  // AskPunttChat, so they must not be duplicated as static cards.
  const feedExtras = useMemo(
    () =>
      (timelineQuery.data?.events ?? [])
        .filter((e) => !isAgentTurnEvent(e))
        .map((e) => ({
          id: e._id,
          createdAt: e.createdAt,
          node: renderProjectFeedCard(e, {
            onResolveSuggestion: (eventId, action, payload) =>
              resolveSuggestion.mutate({ eventId, action, payload }),
            resolvingEventId: resolveSuggestion.isPending
              ? resolveSuggestion.variables?.eventId
              : null,
            onAskPrivately: () => setActiveTab("chats"),
          }),
        }))
        .filter(
          (x): x is { id: string; createdAt: string; node: JSX.Element } =>
            Boolean(x.node),
        ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      timelineQuery.data?.events,
      resolveSuggestion.isPending,
      resolveSuggestion.variables?.eventId,
    ],
  );

  // Reference documents: user-uploaded project files only. Active review
  // cards stay in Reviews.
  const referenceDocuments = useMemo(
    () => projectReferenceDocumentRows(data?.project?.files),
    [data?.project?.files],
  );

  const reviewsPanelData = useMemo(() => {
    const raw = reviewsQuery.data;
    if (!raw) return undefined;
    return {
      ...raw,
      reviews: enrichProjectReviewsFromFiles(raw.reviews, data?.project?.files),
    };
  }, [reviewsQuery.data, data?.project?.files]);

  const syncChatSearch = useCallback(
    (chatId: string | null) => {
      navigate({
        to: "/ask-puntt/projects/$projectId",
        params: { projectId },
        search: { chatId: chatId ?? undefined },
        replace: true,
      });
    },
    [navigate, projectId],
  );

  const selectPersonalChat = useCallback(
    (chatId: string) => {
      setActiveTab("chats");
      setActiveChatId(chatId);
      syncChatSearch(chatId);
    },
    [syncChatSearch],
  );

  // My chats is master-detail: clear any open chat when the project changes so
  // navigating between projects always lands on the chat list, never a stale
  // detail view from the previous project.
  useEffect(() => {
    setActiveChatId(null);
    setActiveTicketId(null);
    setReviewChatCollapsed(false);
  }, [projectId]);

  // Deep-link from sidebar / all-chats list: ?chatId= opens My chats on that thread.
  useEffect(() => {
    const chatId = deepLinkChatId?.trim();
    if (!chatId) return;
    if (!chats.some((c) => c._id === chatId)) return;
    setActiveTab("chats");
    setActiveChatId(chatId);
  }, [deepLinkChatId, chats]);

  useEffect(() => {
    if (projectId)
      analytics.capture("ask_puntt_campaign_viewed", { projectId });
  }, [projectId]);

  const handleCreatePersonalChat = async (
    message: string,
    attachments?: {
      key: string;
      source: string;
      type: string;
      name?: string;
    }[],
  ) => {
    const msg = message.trim();
    if (!msg && (!attachments || attachments.length === 0)) return;
    try {
      const result = await createChat.mutateAsync({
        message: msg,
        workflowType: "general",
        projectId,
        projectChatKind: "personal",
        attachments: attachments?.length ? attachments : undefined,
      });
      analytics.capture("ask_puntt_campaign_personal_chat_created", {
        projectId,
      });
      selectPersonalChat(result.chat._id);
    } catch (error) {
      console.error("Failed to create personal chat:", error);
    }
  };

  const handleOpenTicket = useCallback((ticketId: string) => {
    setActiveTicketId(ticketId);
  }, []);

  // Collapse the app nav sidebar when entering review mode (same as 3-pane chat).
  const prevInReviewRef = useRef(false);
  useEffect(() => {
    if (activeTicketId && !prevInReviewRef.current && appSidebarOpen) {
      dispatch(setDrawerOpen({ app: false }));
    }
    prevInReviewRef.current = Boolean(activeTicketId);
  }, [activeTicketId, appSidebarOpen, dispatch]);

  const handleSaveEdit = async () => {
    try {
      await updateProject.mutateAsync({ description: editDescription });
      setEditOpen(false);
    } catch (error) {
      console.error("Failed to update project context:", error);
    }
  };

  const members = project?.members ?? [];
  const owner = members.find((m) => m._id === project?.ownerId);
  const isOwner = project?.role === "owner";
  const ownerName = project
    ? isOwner
      ? "You"
      : (owner?.name ?? owner?.email ?? "Owner")
    : "";
  const viewer = members.find((m) => m._id === currentUserId);

  const myChats = chats.filter((c) => c._id !== sharedChatId);
  const activeChat = activeChatId
    ? activeChatId === NEW_CHAT_DRAFT_ID
      ? null
      : (chats.find((c) => c._id === activeChatId) ?? null)
    : null;
  const isDraftChat = activeChatId === NEW_CHAT_DRAFT_ID;
  const isPrivateChatDetail =
    isDraftChat || activeChat?.projectChatKind === "personal";

  const feedChat = sharedChatId ? (
    <AskPunttChat
      key={sharedChatId}
      chatId={sharedChatId}
      onProjectMutated={handleProjectMutated}
      onProjectReviewOpen={handleOpenTicket}
      timelineExtras={feedExtras}
      messagePresentation="feed"
      feedActorName={viewer?.name ?? viewer?.email ?? undefined}
      feedActorAvatar={viewer?.avatar ?? undefined}
      feedMembers={members}
      suppressRightPane
    />
  ) : (
    <div
      className="flex flex-1 items-center justify-center"
      data-testid="project-feed-loading"
    >
      <CircleNotch
        size={24}
        weight="bold"
        className="animate-spin text-puntt-neutral-gray-9"
      />
    </div>
  );

  const reviewLeftChat = useMemo(() => {
    if (activeTab === "chats" && activeChatId) {
      if (isDraftChat) {
        return (
          <CampaignNewChatDraft
            data-testid="campaign-new-chat-draft"
            onSubmit={handleCreatePersonalChat}
            isSubmitting={createChat.isPending}
          />
        );
      }
      return (
        <AskPunttChat
          key={activeChatId}
          chatId={activeChatId}
          onProjectMutated={handleProjectMutated}
          onProjectReviewOpen={handleOpenTicket}
          suppressRightPane
        />
      );
    }
    return feedChat;
  }, [
    activeTab,
    activeChatId,
    isDraftChat,
    feedChat,
    createChat.isPending,
    handleProjectMutated,
    handleOpenTicket,
  ]);

  const reviewLeftChatLabel =
    activeTab === "chats" && activeChat && !isDraftChat
      ? (activeChat.title ?? "Chat")
      : "Chat";

  // ---- Resizable review layout (chat | artwork) — mirrors AskPuntt chat.tsx ----
  const reviewPaneContainerRef = useRef<HTMLDivElement | null>(null);
  const reviewContainerWidth = useContainerWidth(reviewPaneContainerRef);
  const reviewWidthOk =
    reviewContainerWidth === null ? false : reviewContainerWidth >= 1024;
  const useResizableReviewLayout =
    reviewWidthOk && Boolean(activeTicketId) && !reviewChatCollapsed;
  const { prefs: uiPrefs, setPref: setUiPref } = useUiPrefs();
  const [reviewActiveDragId, setReviewActiveDragId] = useState<string | null>(
    null,
  );
  const reviewPanelGroupRef = useRef<ImperativePanelGroupHandle>(null);
  const REVIEW_2PANE_DEFAULT: number[] = [28, 72];
  const REVIEW_2PANE_MINS: number[] = [18, 40];
  const reviewTwoPaneLayout = usePersistedLayout({
    storageKey: "askpuntt-project-review-2pane",
    defaultLayout: REVIEW_2PANE_DEFAULT,
    minSizes: REVIEW_2PANE_MINS,
  });
  const reviewDragAbortSignal = `${activeTicketId ?? ""}:${
    reviewChatCollapsed ? "collapsed" : "expanded"
  }`;
  const reviewDragInProgress = reviewActiveDragId !== null;

  const reviewChatPanelInner = (
    <>
      <div className="flex h-12 shrink-0 items-center gap-2 border-b border-puntt-neutral-gray-5 px-4">
        <h2
          className="flex-1 truncate text-[11px] font-bold uppercase tracking-wide text-puntt-neutral-gray-11"
          data-testid="campaign-review-chat-heading"
        >
          {reviewLeftChatLabel}
        </h2>
        <ChevronNavButton
          direction="left"
          onClick={() => setReviewChatCollapsed(true)}
          aria-label="Collapse chat"
          data-testid="campaign-review-chat-collapse"
        />
      </div>
      <div
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
        data-testid="campaign-review-chat-body"
      >
        {activeTab === "chats" && isPrivateChatDetail && (
          <CampaignChatPrivacyBanner />
        )}
        <div className="min-h-0 flex-1 overflow-hidden">{reviewLeftChat}</div>
      </div>
    </>
  );

  if (isLoading) {
    return (
      <div
        className="flex h-full items-center justify-center bg-base-white"
        data-testid="campaign-loading"
      >
        <CircleNotch
          size={28}
          weight="bold"
          className="animate-spin text-puntt-neutral-gray-9"
        />
      </div>
    );
  }

  if (isError || !project) {
    return (
      <div
        className="flex h-full flex-col items-center justify-center gap-4 bg-base-white"
        data-testid="campaign-error"
      >
        <Text size="4" weight="medium" className="text-puntt-neutral-gray-11">
          We couldn&apos;t load this project
        </Text>
        <Link
          to={PROJECTS_LAUNCHER_PATH}
          className="text-sm text-puntt-accent-9 hover:underline"
          data-testid="campaign-error-back"
        >
          ← Back to all projects
        </Link>
      </div>
    );
  }

  const openEdit = () => {
    setEditDescription(project.description ?? "");
    setEditOpen(true);
  };

  const activeReview = reviewsPanelData?.reviews.find(
    (r) => r.ticketId === activeTicketId,
  );
  const activeReviewHasContext = (activeReview?.contextAssets?.length ?? 0) > 0;
  const reviewWorkspaceMinWidthPx = activeTicketId
    ? reviewWorkspaceMinWidth({
        chatCollapsed: reviewChatCollapsed,
        contextCollapsed: false,
        hasContext: activeReviewHasContext,
      })
    : undefined;

  return (
    <div
      className="flex size-full min-h-0 min-w-0 flex-col overflow-hidden"
      data-testid="ask-puntt-campaign"
    >
      {/* Header — viewport-bound, outside horizontal scroll */}
      <header className="flex h-14 w-full min-w-0 max-w-full shrink-0 items-center gap-3 overflow-hidden border-b border-puntt-neutral-gray-5 px-5">
        <Link
          to={PROJECTS_LAUNCHER_PATH}
          aria-label="Back to all projects"
          data-testid="campaign-back-link"
          className="flex shrink-0 items-center gap-1 text-sm font-medium text-puntt-accent-9 transition-colors hover:text-puntt-accent-10 hover:underline"
        >
          <ArrowLeft size={16} weight="bold" aria-hidden="true" />
          All projects
        </Link>
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <h1
            className="min-w-0 truncate text-[15px] font-semibold text-puntt-neutral-gray-12"
            data-testid="campaign-title"
          >
            {project.name}
          </h1>
          <span
            className="hidden shrink-0 rounded-full bg-puntt-neutral-gray-3 px-2 py-0.5 text-[11px] font-medium text-puntt-neutral-gray-11 sm:inline"
            data-testid="campaign-owner-badge"
          >
            Owned by {ownerName}
          </span>
        </div>
        {members.length > 0 && (
          <div
            className="flex shrink-0 items-center -space-x-2"
            data-testid="campaign-members"
            aria-label={`${members.length} project members`}
          >
            {members.slice(0, 5).map((m) => (
              <AvatarWithInitials
                key={m._id}
                size={7}
                name={m.name ?? m.email ?? "Member"}
                avatar={m.avatar ?? undefined}
                className="ring-2 ring-puntt-neutral-gray-1"
              />
            ))}
            {members.length > 5 && (
              <span
                className="flex size-7 items-center justify-center rounded-full bg-puntt-neutral-gray-3 text-[10px] font-medium text-puntt-neutral-gray-11 ring-2 ring-puntt-neutral-gray-1"
                data-testid="campaign-members-overflow"
              >
                +{members.length - 5}
              </span>
            )}
          </div>
        )}
        <button
          type="button"
          onClick={() => setInviteOpen(true)}
          aria-label="Invite to project"
          data-testid="project-invite-button"
          className="flex shrink-0 items-center gap-1.5 rounded-md border border-puntt-neutral-gray-7 bg-base-white px-2.5 py-1.5 text-[13px] font-medium text-puntt-neutral-gray-12 transition-colors hover:border-puntt-accent-9 hover:bg-puntt-blue-2 hover:text-puntt-accent-9"
        >
          <UserPlus size={15} />
          Invite
        </button>
      </header>

      {!activeTicketId && (
        <div
          role="tablist"
          aria-label="Project views"
          className="flex shrink-0 gap-8 border-b border-puntt-neutral-gray-5 bg-base-white px-5"
        >
          <TabButton
            label="Project feed"
            data-testid="project-feed-tab"
            active={activeTab === "feed"}
            onClick={() => setActiveTab("feed")}
          />
          <TabButton
            label="My chats"
            data-testid="project-my-chats-tab"
            active={activeTab === "chats"}
            onClick={() => setActiveTab("chats")}
          />
        </div>
      )}

      {activeTicketId ? (
        <div
          className="flex min-h-0 w-full min-w-0 flex-1 overflow-x-auto overflow-y-hidden"
          data-testid="campaign-body-scroll"
          aria-label="Project workspace"
        >
          <div
            className="flex size-full min-h-0"
            data-testid="campaign-workspace-row"
            style={
              reviewWorkspaceMinWidthPx != null
                ? reviewWorkspaceRowStyle(reviewWorkspaceMinWidthPx)
                : undefined
            }
          >
            <div
              ref={reviewPaneContainerRef}
              className="relative flex min-h-0 min-w-0 flex-1 overflow-hidden"
              data-testid="campaign-review-mode"
            >
              {useResizableReviewLayout ? (
                <PanelGroup
                  direction="horizontal"
                  ref={reviewPanelGroupRef}
                  onLayout={reviewTwoPaneLayout.onLayout}
                  className={`flex flex-1 ${reviewDragInProgress ? "[&_*]:!transition-none" : ""}`}
                >
                  <Panel
                    id="chat"
                    order={0}
                    minSize={REVIEW_2PANE_MINS[0]}
                    defaultSize={reviewTwoPaneLayout.layout[0]}
                  >
                    <div
                      className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden border-r border-puntt-neutral-gray-5"
                      data-testid="pane-chat"
                    >
                      {reviewChatPanelInner}
                    </div>
                  </Panel>
                  <ResizeHandle
                    id="chat-right"
                    panelGroupRef={reviewPanelGroupRef}
                    defaultLayout={REVIEW_2PANE_DEFAULT}
                    abortSignal={reviewDragAbortSignal}
                    onDraggingChange={(d) =>
                      setReviewActiveDragId(d ? "chat-right" : null)
                    }
                  />
                  <Panel
                    id="review"
                    order={1}
                    minSize={REVIEW_2PANE_MINS[1]}
                    defaultSize={reviewTwoPaneLayout.layout[1]}
                  >
                    <CampaignReviewPane
                      ticketId={activeTicketId}
                      onClose={() => setActiveTicketId(null)}
                    />
                  </Panel>
                </PanelGroup>
              ) : (
                <>
                  {reviewChatCollapsed ? (
                    <button
                      type="button"
                      onClick={() => setReviewChatCollapsed(false)}
                      aria-label="Expand chat"
                      aria-expanded={false}
                      data-testid="campaign-review-chat-expand"
                      className="flex w-8 shrink-0 flex-col items-center gap-2 border-r border-puntt-neutral-gray-5 bg-puntt-neutral-gray-1 py-3 text-puntt-neutral-gray-11 transition-colors hover:bg-puntt-neutral-gray-3 hover:text-puntt-neutral-gray-12"
                    >
                      <CaretRight size={14} aria-hidden="true" />
                      <span className="text-[11px] font-bold uppercase tracking-wide [writing-mode:vertical-rl]">
                        Chat
                      </span>
                    </button>
                  ) : (
                    <div
                      className="flex shrink-0 flex-col border-r border-puntt-neutral-gray-5"
                      style={{
                        width: `min(${REVIEW_CHAT_FALLBACK_PX}px, 30vw)`,
                      }}
                    >
                      {reviewChatPanelInner}
                    </div>
                  )}
                  <CampaignReviewPane
                    ticketId={activeTicketId}
                    onClose={() => setActiveTicketId(null)}
                  />
                </>
              )}
              <ResizeHintTooltip
                alreadySeen={Boolean(uiPrefs.resizeHintSeen)}
                onShown={() => setUiPref("resizeHintSeen", true)}
                resizableActive={useResizableReviewLayout}
              />
            </div>
          </div>
        </div>
      ) : (
        <div
          className="flex min-h-0 flex-1 overflow-hidden"
          data-testid="campaign-body-scroll"
          aria-label="Project workspace"
        >
          <div
            className="flex size-full min-h-0 min-w-0"
            data-testid="campaign-workspace-row"
          >
            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
              <div className="flex min-h-0 flex-1 overflow-hidden">
                {activeTab === "feed" ? (
                  <div
                    className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
                    data-testid="project-feed-panel"
                  >
                    {feedChat}
                  </div>
                ) : (
                  <div
                    className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
                    data-testid="my-chats-panel"
                  >
                    {activeChatId ? (
                      <div
                        className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
                        data-testid="campaign-chat-area"
                      >
                        <CampaignChatDetailHeader
                          title={
                            isDraftChat
                              ? "New chat"
                              : (activeChat?.title ?? "Chat")
                          }
                          isShared={activeChat?.projectChatKind === "shared"}
                          onBack={() => {
                            setActiveChatId(null);
                            syncChatSearch(null);
                          }}
                          onShare={() => {
                            if (activeChatId && !isDraftChat) {
                              shareChat.mutate(activeChatId);
                            }
                          }}
                          isSharing={shareChat.isPending}
                          shareDisabled={isDraftChat}
                        />
                        {isPrivateChatDetail && <CampaignChatPrivacyBanner />}
                        {isDraftChat ? (
                          <CampaignNewChatDraft
                            data-testid="campaign-new-chat-draft"
                            onSubmit={handleCreatePersonalChat}
                            isSubmitting={createChat.isPending}
                          />
                        ) : (
                          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                            <AskPunttChat
                              key={activeChatId}
                              chatId={activeChatId}
                              onProjectMutated={handleProjectMutated}
                              onProjectReviewOpen={handleOpenTicket}
                              suppressRightPane
                            />
                          </div>
                        )}
                      </div>
                    ) : (
                      <CampaignChatList
                        chats={myChats}
                        currentUserId={currentUserId}
                        members={members}
                        onSelect={selectPersonalChat}
                        onNewChat={() => {
                          setActiveChatId(NEW_CHAT_DRAFT_ID);
                          syncChatSearch(null);
                        }}
                        isCreating={createChat.isPending}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>

            <CollapsedPaneLabel
              collapsed={!rightRailExpanded}
              onExpand={() => {
                setReviewsSectionOpen(true);
                setContextSectionOpen(true);
              }}
              ariaLabel="Show reviews and project context"
              testId="campaign-right-rail-expand"
              labelRender={() => "Context"}
            />
            {rightRailExpanded ? (
              <aside
                className="flex h-full min-h-0 shrink-0 flex-col gap-3 overflow-hidden border-l border-puntt-neutral-gray-5 bg-puntt-neutral-gray-2 p-3"
                style={{ width: PROJECT_RAIL_WIDTH_PX }}
                data-testid="campaign-right-rail"
                data-expanded="true"
              >
                <div
                  className={`flex flex-col overflow-hidden ${reviewsSectionOpen ? "max-h-[45%] min-h-32 shrink-0" : "shrink-0"}`}
                  data-testid="campaign-reviews-rail-slot"
                  data-section-expanded={reviewsSectionOpen ? "true" : "false"}
                >
                  <ProjectReviewsPanel
                    data={reviewsPanelData}
                    isLoading={reviewsQuery.isLoading}
                    open={reviewsSectionOpen}
                    onOpenChange={setReviewsSectionOpen}
                    onOpenReview={(item) => {
                      if (item.ticketId) handleOpenTicket(item.ticketId);
                    }}
                  />
                </div>
                <div
                  className={`flex flex-col overflow-hidden ${contextSectionOpen ? "min-h-0 flex-1" : "shrink-0"}`}
                  data-testid="campaign-context-rail-slot"
                  data-section-expanded={contextSectionOpen ? "true" : "false"}
                >
                  <CampaignDocsPanel
                    documents={referenceDocuments}
                    description={project.description}
                    upcomingWork={project.upcomingWork}
                    onEdit={isOwner ? openEdit : undefined}
                    panelOpen={contextSectionOpen}
                    onPanelOpenChange={setContextSectionOpen}
                  />
                </div>
              </aside>
            ) : null}
          </div>
        </div>
      )}

      {/* Invite modal */}
      <ProjectInviteDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        projectId={projectId}
        projectName={project.name}
        members={members}
      />

      {/* Edit project context dialog (owner-only) */}
      <Dialog.Root open={editOpen} onOpenChange={setEditOpen}>
        <Dialog.Content
          maxWidth="500px"
          data-testid="project-context-edit-dialog"
          aria-label="Edit project context"
        >
          <Dialog.Title>Edit project context</Dialog.Title>
          <Dialog.Description size="2" className="text-puntt-neutral-gray-11">
            Update the project description Puntt uses as context.
          </Dialog.Description>
          <TextArea
            data-testid="project-context-edit-input"
            aria-label="Project description"
            className="mt-3"
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            style={{ height: 120 }}
          />
          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button
                variant="soft"
                color="gray"
                data-testid="project-context-edit-cancel"
              >
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              data-testid="project-context-edit-save"
              disabled={updateProject.isPending}
              onClick={handleSaveEdit}
            >
              {updateProject.isPending ? (
                <CircleNotch size={14} weight="bold" className="animate-spin" />
              ) : null}
              Save
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </div>
  );
}

function TabButton({
  label,
  active,
  onClick,
  "data-testid": dataTestid,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  "data-testid": string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      data-testid={dataTestid}
      data-active={active ? "true" : "false"}
      onClick={onClick}
      className={`-mb-px border-b-[3px] bg-transparent px-0.5 py-3 text-[13px] transition-colors hover:bg-transparent ${
        active
          ? "border-puntt-accent-9 font-semibold text-puntt-accent-9"
          : "border-transparent font-normal text-puntt-neutral-gray-10 hover:text-puntt-neutral-gray-11"
      }`}
    >
      {label}
    </button>
  );
}
